import React from 'react';
import ProductCard from '../ProductCard/ProductCard';
import _chunk from 'lodash/chunk';
import { ProductEntity } from 'src/components/BeaconV2Components/ExperimentalLayout/types';
import { getProductImageUrl } from 'src/utils/image';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import ProductGridLoading from './ProductGridLoading';
import { AppName, ValueOf } from 'sl-api-connector';
import { calculatePercentChange, getAppName } from 'src/utils/app';
import { Direction, getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import _get from 'lodash/get';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';

export const GRID_ITEMS_PER_PAGE = 20;
const NUM_COLUMNS = 4;

export type ProductGridDatum = {
  product: ProductEntity;
} & {
  [fieldName: string]: {
    value: number;
    difference: number;
  };
};

export interface ProductGridProps {
  data: ProductGridDatum[];
  showExcludeProductButton?: boolean;
  loading: boolean;
  metricType: ValueOf<typeof METRICTYPE>;
  page: number;
  fieldName: string;
  mainMetricOverride?: (metricValue: number, dataRow: any) => string;
  secondaryMetricOverride?: (valueDifference: number, dataRow: any) => string;
  directionOverride?: (dataRow: any) => Direction | null;
  showDirectionOverride?: (dataRow: any) => boolean;
  handleExcludeProduct?: (retailerSku: string) => void;
  noDataPlaceholder?: React.ReactNode;
}

/**
 * Takes a list of products and renders product cards in a grid.
 */
const ProductGrid = ({
  data,
  loading,
  metricType,
  page,
  mainMetricOverride,
  secondaryMetricOverride,
  directionOverride,
  showDirectionOverride,
  fieldName,
  showExcludeProductButton,
  handleExcludeProduct,
  noDataPlaceholder
}: ProductGridProps) => {
  const formatMetric = useMetricFormatter();
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
  const { targetUrl, name } = useAppSelector((state) => state.app);
  const retailerId = useAppSelector((state) => state.retailer.id);
  const appName = getAppName();

  // Atlas product URLs should include tab=scorecardV2&subtab=all if not already present
  const existingParams = `${searchParams}${additionalParams}`;
  const hasTabParam = existingParams.includes('tab=');
  const atlasProductParams = appName === AppName.Atlas && !hasTabParam ? '&tab=scorecardV2&subtab=all' : '';

  return (
    <Flex flexDirection="column" width="100%">
      {loading ? (
        <ProductGridLoading />
      ) : data.length < 1 && noDataPlaceholder ? (
        noDataPlaceholder
      ) : (
        <Flex flexDirection="column">
          {_chunk(
            data.slice((page - 1) * GRID_ITEMS_PER_PAGE, (page - 1) * GRID_ITEMS_PER_PAGE + GRID_ITEMS_PER_PAGE),
            NUM_COLUMNS
          ).map((items, rowIndex) => (
            <Flex
              key={items.map((i) => i.product?.stacklineSku).join('')}
              flexDirection="row"
              gap={16}
              justifyContent="flex-start"
            >
              {items.map((row, colIndex) => {
                const indexOnPage = rowIndex * NUM_COLUMNS + colIndex;
                const dataIndex = (page - 1) * GRID_ITEMS_PER_PAGE + indexOnPage;
                const { product } = row;
                const metricValue = _get(row, [fieldName, 'value'], 0);
                const valueDifference = _get(row, [fieldName, 'difference'], 0);

                if (!product) {
                  return null;
                }
                return (
                  <Flex key={product?.stacklineSku} marginBottom={'16px'}>
                    <ProductCard
                      availabilityStatusCode={product.availabilityStatusCode}
                      brandUrl={`/brand/${product.brandId}${searchParams}${additionalParams}${atlasProductParams}`}
                      categoryName={product.categoryName}
                      categoryUrl={`/category/${product.categoryId}${searchParams}${additionalParams}${atlasProductParams}`}
                      direction={directionOverride ? directionOverride(data[dataIndex]) : getDirection(valueDifference)}
                      showDirection={showDirectionOverride ? showDirectionOverride(data[dataIndex]) : true}
                      imageUri={getProductImageUrl(product?.stacklineSku)}
                      mainDisplayTitle={product.brandName}
                      showExcludeProductButton={showExcludeProductButton}
                      handleExcludeProduct={handleExcludeProduct}
                      mainMetric={`${
                        mainMetricOverride
                          ? mainMetricOverride(metricValue, data[dataIndex])
                          : formatMetric(metricValue, metricType, {
                              showFullValue: metricType === METRICTYPE.PERCENT,
                              decimalPlaces: 1,
                              showNegative: true
                            })
                      }`}
                      parentBrandName={product?.parentBrandName ?? ''}
                      productUrl={`/product/${product?.stacklineSku}${searchParams}${additionalParams}${atlasProductParams}`}
                      retailerSku={product.retailerSku}
                      retailerUrl={`https://${targetUrl}/api/utility/OpenRetailerProductPage?appName=${name}&retailerId=${retailerId}&stacklineSku=${product.stacklineSku}`}
                      secondaryDisplayTitle={product.title}
                      secondaryMetric={
                        secondaryMetricOverride
                          ? secondaryMetricOverride(valueDifference, data[dataIndex])
                          : `${formatMetric(Math.abs(valueDifference), metricType, {
                              decimalPlaces: 1,
                              showFullValue: metricType === METRICTYPE.PERCENT
                            })} (${formatMetric(
                              calculatePercentChange(metricValue, metricValue - valueDifference),
                              METRICTYPE.PERCENT,
                              {
                                formatString: '0,0%',
                                showFullValue: true
                              }
                            )})`
                      }
                      stacklineSku={product?.stacklineSku}
                      statusColor={product.availabilityStatusCode === 'In stock' ? 'success' : 'error'}
                      subCategoryName={product.subCategoryName}
                      subcategoryUrl={`/subcategory/${product.subCategoryId}${searchParams}${additionalParams}${atlasProductParams}`}
                    />
                  </Flex>
                );
              })}
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default ProductGrid;
